import {objectType} from "_src/constants";
import {pixelBufferType} from "_src/engine/constants";
import GroupableWrapper from "_src/groupable_wrapper";
import loader from "_src/loader";
import output from "_src/engine/output";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import loadingProgress from "_src/loading_progress";
import containersCache from "_src/engine/packers/containers_cache";
import reformer from "_src/reformer/reformer";

class PhotoWrapper {
  constructor(selfPropertyGetter) {
    this.mip = null;
    this.fullPhoto = null;
    this.fullPhotoPromise = null;
    this._selfPropertyGetter = selfPropertyGetter;
  }

  getProperty(name) {
    return this._selfPropertyGetter(name);
  }
}

export default class PhotoGroupWrapper extends GroupableWrapper {
  constructor(params) {
    super(params);
    this.type = objectType.PHOTO_GROUP;
    this.photos = new Map();
    this.previewData = {};

    this.undistortion = null;
    this._undistortionPromise = null;
  }

  getHash() {
    return this._selfPropertyGetter("photosSource")["hash"];
  }

  async _loadData() {
    const loadingFiles = new Set();
    const loadingPromises = [];
    for (const photoData of this._selfPropertyGetter("photos")) {
      const photoObject = new PhotoWrapper(name => photoData[name]);
      this.photos.set(photoData["id"], photoObject);

      const photoDescriptor = photoData["textures"][0]["levels"][1];
      const file = photoDescriptor["file"];
      const id = photoDescriptor["index"];
      const payload = {
        objectID: -1,
        frameID: -1,
        id: id,
        releaseAfterUse: false,
      };
      loadingFiles.add(file);

      const photoPath = reformer.getProjectDirectory() + file;
      const promise = loader.load(photoPath, loader.IMAGE, payload).then(image => {
        if (!image) {
          output.error(output.DEVELOPERS_ERROR, "Can't load photo on path: " + photoPath);
          return;
        }
        const texture = bufferFactory.create(pixelBufferType.T_RGBA,
            {
              width : image.width,
              height : image.height,
              immutable : true,
            }
        );

        texture.initMipMapLevelImage(0, image);
        photoObject.mip = texture;

        this.previewData[photoData["id"]] = image.getImage();
        loadingProgress.updatePhotoGroupStatus(this);
      });
      loadingPromises.push(promise);
    }

    await Promise.all(loadingPromises);

    for (const file of loadingFiles) {
      containersCache.releaseContainer(file);
    }
  }

  _unloadData() {
    this.previewData = {};
    this.photos.clear();
    this.undistortion = null;
    this._undistortionPromise = null;
  }

  removePhoto(id, dataFiles) {
    const photosData = this._selfPropertyGetter("photos");
    const photoData = photosData.find(photo => photo["id"] === id);
    if (photoData) {
      dataFiles.push(photoData["textures"][0]["levels"][0].file);
      dataFiles.push(photoData["textures"][0]["levels"][1].file);
    }

    this.photos.delete(id);
    this._selfPropertySetter("photos", photosData.filter(photo => photo["id"] !== id));
    this._selfPropertyGetter("photosSource")["hash"] = "";
  }

  async initPhoto(id, progressCallback) {
    const photo = this.photos.get(id);
    if (photo.fullPhoto) {
      progressCallback(this.getID(), id, 1);
      return photo.fullPhoto;
    }

    if (!photo.fullPhotoPromise) photo.fullPhotoPromise = this._initPhoto(id, progressCallback);
    return photo.fullPhotoPromise;
  }

  async initUndistortion() {
    if (this.undistortion) return this.undistortion;

    if (!this._undistortionPromise) this._undistortionPromise = this._initUndistortion();
    return this._undistortionPromise;
  }

  getPhotoFiles(photoIDs) {
    const photos = this._selfPropertyGetter("photos");
    const photoFiles = [];
    for (const photoID of photoIDs) {
      const photoData = photos.find(photo => photo["id"] === photoID);
      const photoFile = photoData ? {
        file: photoData["textures"][0]["levels"][0]["file"],
        index: photoData["textures"][0]["levels"][0]["index"]
      } : null;
      photoFiles.push(photoFile);
    }

    return photoFiles;
  }

  async _initUndistortion() {
    const descriptor = this._selfPropertyGetter("undistortionMap");
    if (!descriptor) return null;

    const file = descriptor.getValue("file");
    const index = descriptor.getValue("index");
    const payload = {
      objectID: this.getID(),
      frameID: -1,
      id: index,
    };
    const fullPath = reformer.getProjectDirectory() + file;

    let image;
    try {
      image = await loader.load(fullPath, loader.IMAGE, payload);
      if (!image) {
        output.error(output.DEVELOPERS_ERROR, "Can't load undistortion map on path: " + fullPath);
        return null;
      }
    } catch (e) {
      output.error(output.DEVELOPERS_ERROR, "Can't load undistortion map on path: " + fullPath);
      return null;
    }

    const texture = bufferFactory.create(pixelBufferType.T_RG32F,
        {
          width : image.width,
          height : image.height,
        }
    );

    texture.initMipMapLevelBuffer(0, image.getBuffer());
    containersCache.releaseContainer(file);
    this.undistortion = texture;
    return this.undistortion;
  }

  async _initPhoto(photoID, progressCallback) {
    const photoData = this._selfPropertyGetter("photos").find(photo => photo["id"] === photoID);
    const photoDescriptor = photoData["textures"][0]["levels"][0];
    const file = photoDescriptor["file"];
    const index = photoDescriptor["index"];
    const fullPath = reformer.getProjectDirectory() + file;

    const loadingCallback = progress => progressCallback(this.getID(), photoID, progress);
    const payload = {
      objectID: this.getID(),
      frameID: photoID,
      id: index,
      fileProgress: loadingCallback,
    };

    let image;
    try {
      image = await loader.load(fullPath, loader.IMAGE, payload);
      if (!image) {
        output.error(output.DEVELOPERS_ERROR, "Can't load photo on path: " + fullPath);
        return null;
      }
    } catch (error) {
      if (!loader.isLegalError(error)) {
        output.error(output.DEVELOPERS_ERROR, "Can't load photo: " + photoID);
        return null;
      }
    }
    const texture = bufferFactory.create(pixelBufferType.T_RGBA,
        {
          width : image.width,
          height : image.height,
          immutable : true,
        }
    );

    const buffer = image.getBuffer();
    if (buffer) {
      texture.initMipMapLevelBuffer(0, buffer);
    } else {
      texture.initMipMapLevelImage(0, image);
    }

    this.photos.get(photoID).fullPhoto = texture;
    containersCache.releaseContainer(file);
    progressCallback(this.getID(), photoID, 1);

    return texture;
  }
}
